// src/components/ui/sections/CommunicationsTemplatesList/CommunicationsTemplatesListShadcn.jsx
import React from 'react';
import {
  Mail,
  MessageSquare,
  Search,
  Plus
} from 'lucide-react';

// shadcn components
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../card';
import { Button } from '../../button';
import { Input } from '../../input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../tabs';

// Import our custom components
import CommunicationsTemplateCardShadcn from '../../cards/CommunicationsTemplateCard/CommunicationsTemplateCardShadcn';

import { cn } from '../../../../utils/cn';

const CommunicationsTemplatesListShadcn = ({
  emailTemplates = [],
  smsTemplates = [],
  selectedTemplateId,
  onSelectTemplate,
  onCreateTemplate,
  searchTerm,
  onSearchChange,
  templateType,
  onTemplateTypeChange,
  showInactive,
  onToggleInactive,
  loading = false,
  canManageTemplates = true
}) => {
  // Template type filter options
  const templateTypeOptions = [
    {
      value: 'email',
      label: 'Email',
      icon: Mail,
      count: emailTemplates.length,
      color: 'text-blue-400'
    },
    {
      value: 'sms',
      label: 'SMS',
      icon: MessageSquare,
      count: smsTemplates.length,
      color: 'text-green-400'
    }
  ];

  // Get current templates based on type
  const currentTemplates = templateType === 'email' ? emailTemplates : smsTemplates;

  // Filter templates based on search - always show all templates regardless of active status
  const filteredTemplates = currentTemplates.filter(template => {
    const matchesSearch = !searchTerm ||
      template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.template_key.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (templateType === 'email' && template.subject?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (templateType === 'sms' && template.message_body?.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus = true; // Always show all templates regardless of active status

    return matchesSearch && matchesStatus;
  });

  return (
    <Card className="h-full flex flex-col">
      {/* Header */}
      <CardHeader className="flex-shrink-0">
        <div className="flex items-center justify-between mb-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              {templateType === 'email' ? (
                <Mail className="w-5 h-5 text-blue-400" />
              ) : (
                <MessageSquare className="w-5 h-5 text-green-400" />
              )}
              Templates
            </CardTitle>
            <CardDescription>
              Manage your communication templates
            </CardDescription>
          </div>

          {canManageTemplates && (
            <Button
              onClick={() => onCreateTemplate()}
              disabled={loading}
              size="sm"
            >
              <Plus className="mr-2 h-4 w-4" />
              New
            </Button>
          )}
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search templates..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Template Type Filter - Using Tabs */}
        <Tabs value={templateType} onValueChange={onTemplateTypeChange} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            {templateTypeOptions.map((option) => {
              const Icon = option.icon;
              return (
                <TabsTrigger key={option.value} value={option.value} className="flex items-center gap-2">
                  <Icon className={cn("w-4 h-4", option.color)} />
                  <span>{option.label}</span>
                  <span className="ml-1 px-1.5 py-0.5 rounded-full text-xs font-semibold bg-muted text-muted-foreground">
                    {option.count}
                  </span>
                </TabsTrigger>
              );
            })}
          </TabsList>
        </Tabs>
      </CardHeader>

      {/* Templates List */}
      <CardContent className="flex-1 min-h-0 overflow-y-auto space-y-3">
        <Tabs value={templateType} className="h-full">
          <TabsContent value="email" className="h-full m-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="w-8 h-8 border-2 border-border border-t-primary rounded-full animate-spin mx-auto mb-2"></div>
                  <p className="text-muted-foreground text-sm">Loading templates...</p>
                </div>
              </div>
            ) : filteredTemplates.length === 0 ? (
              <div className="text-center py-8">
                <Mail className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <h3 className="text-base font-medium text-foreground mb-2">
                  {searchTerm
                    ? 'No templates found'
                    : 'No email templates yet'
                  }
                </h3>
                <p className="text-muted-foreground text-sm mb-4">
                  {searchTerm
                    ? 'Try adjusting your search.'
                    : 'Create your first email template.'
                  }
                </p>
                {canManageTemplates && !searchTerm && (
                  <Button
                    onClick={() => onCreateTemplate()}
                    size="sm"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Create Template
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {filteredTemplates.map((template) => (
                  <CommunicationsTemplateCardShadcn
                    key={template.id}
                    template={template}
                    templateType={templateType}
                    isSelected={selectedTemplateId === template.id}
                    onClick={() => onSelectTemplate(template.id)}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="sms" className="h-full m-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="w-8 h-8 border-2 border-border border-t-primary rounded-full animate-spin mx-auto mb-2"></div>
                  <p className="text-muted-foreground text-sm">Loading templates...</p>
                </div>
              </div>
            ) : filteredTemplates.length === 0 ? (
              <div className="text-center py-8">
                <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <h3 className="text-base font-medium text-foreground mb-2">
                  {searchTerm
                    ? 'No templates found'
                    : 'No SMS templates yet'
                  }
                </h3>
                <p className="text-muted-foreground text-sm mb-4">
                  {searchTerm
                    ? 'Try adjusting your search.'
                    : 'Create your first SMS template.'
                  }
                </p>
                {canManageTemplates && !searchTerm && (
                  <Button
                    onClick={() => onCreateTemplate()}
                    size="sm"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Create Template
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {filteredTemplates.map((template) => (
                  <CommunicationsTemplateCardShadcn
                    key={template.id}
                    template={template}
                    templateType={templateType}
                    isSelected={selectedTemplateId === template.id}
                    onClick={() => onSelectTemplate(template.id)}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default CommunicationsTemplatesListShadcn;